import _ from 'lodash'
import {
  getMergedModels,
  updateImageV2Converter
} from '@vzmi/shp_shared_compo/helpers/proposalConverters'
import { PROPOSAL_TYPE } from '@vzmi/shp_shared_compo/constants/proposal'
import {
  EXTRA_MODEL_VALUES,
  EXTRA_MODEL_GROUP
} from '../../../../constants/proposalTypes'
import { syncApplicantByIdentity } from '../../Common/ProposalForm/Shared/util'
import { setModelImages, getOrderUniqueImages } from '../../Common/ProposalForm/Shared/modelsUtils'

export const RENDER_ID = 'renderId'

/**
 * 無屬性賣場的話，自己組合出 { sku, images } 推入 models 中
 */
export const adjustModels = listing => {
  const models = _.get(listing, 'models', [])
  const isNoModelsListing = _.isEmpty(models)
  if (isNoModelsListing) {
    _.set(listing, 'isNoModelsListing', true)

    const {
      videos,
      images,
      sku
    } = listing

    _.set(listing, 'models', [{ sku, images, videos }])
  }

  return listing
}

/**
 * 將 Group 贈品轉成一般 model 的形式
 */
const _transformToNormalModel = modelInGroup => {
  const {
    groupName: name,
    groupSku: sku,
    images
  } = modelInGroup

  return {
    sku,
    name,
    images
  }
}

export const getExtraModelMap = listing => {
  const shareMediaBetweenModels = _.get(listing, 'shareMediaBetweenModels')
  const models = _.get(listing, 'models', [])
  const firstModelImages = _.get(models, '[0].images', [])
  const modelMapBySku = _.keyBy(models, 'sku')
  const extraModelMap = _.reduce(_.values(EXTRA_MODEL_GROUP), (map, group) => {
    const { relatedModel } = group
    let currentModels = _.get(listing, relatedModel, [])
    if (_.isEmpty(currentModels)) {
      return map
    }

    currentModels = _.map(currentModels, model => {
      const currentExtraModelSku = _.get(model, 'sku')
      // 如果贈品、加價購商品和賣場商品一樣(買一送一)，則商品圖要直接共用
      const sameModelInListingModel = _.get(modelMapBySku, currentExtraModelSku, {})
      // 判斷贈品、加價購商品是否在賣場有一樣的商品
      if (!_.isEmpty(sameModelInListingModel)) {
        return {
          ...model,
          images: shareMediaBetweenModels
            // 賣場商品共用商品圖，第一個商品圖即為目標圖片
            ? firstModelImages
            // 賣場商品未共用商品圖，找到對應 sku 的商品圖片即目標圖片
            : _.get(sameModelInListingModel, 'images', [])
        }
      }

      // 贈品、加價購沒有和賣場商品相同的商品時，贈品、加價購沒有商品圖不顯示在 ImageFunctionTable 上
      if (_.isEmpty(model.images)) {
        return null
      }

      return model
    })
    map[relatedModel] = _.filter(currentModels, model => !_.isNull(model))
    return map
  }, {})
  return extraModelMap
}

/**
 * 將賣場中的 Group 贈品的圖片合併到對應的贈品類別下
 */
export const adjustImages = (listing, proposalType) => {
  const extraModelMap = getExtraModelMap(listing)

  _.forEach(EXTRA_MODEL_GROUP, GROUP => {
    const {
      value,
      relatedModel
    } = GROUP
    let relatedModels = _.get(extraModelMap, relatedModel, null)

    if (!_.isNull(relatedModels)) {
      const group = _.get(listing, value, null)

      let normalModels = []
      if (!_.isNull(group)) {
        // 賣場屬性調整會用到 extra models 全部欄位，所以不走舊路移除或轉換部分 extra models 資料
        if (proposalType === PROPOSAL_TYPE.UPDATE_LISTING_MODEL) {
          normalModels = _.get(listing, relatedModel, [])
          relatedModels = []
        } else {
          normalModels = group.map(_transformToNormalModel)
          delete listing[value]
        }
      }

      _.set(listing, relatedModel, [...relatedModels, ...normalModels])
    }
  })

  return listing
}

export const adjustListingInPlaceWith = transformers => _.flow(transformers)

export const transformFetchedToLoadingData = (proposal, isConvertOriGd) => {
  const {
    type,
    setting,
    applicant,
    subStationId,
    listing: {
      models: modelsFromListing = []
    } = []
  } = proposal

  // get 提案人
  const identity = _.get(setting, 'user.identity', {})
  const syncedApplicant = syncApplicantByIdentity(applicant, identity)

  // get 提案站別
  const subStationOptions = _.get(setting, 'subStationOptions', [])
  const authorizedSubstation = _.find(subStationOptions, o => o.value === subStationId)
  const subStationLabel = _.get(authorizedSubstation, 'label')

  const clonedProposal = _.cloneDeep(proposal)

  switch (type) {
    // 修改圖片
    case PROPOSAL_TYPE.UPDATE_IMAGE: {
      const { listing } = clonedProposal
      const { models = [] } = listing

      models.forEach(setModelImages)

      EXTRA_MODEL_VALUES.forEach(extraModelName => {
        const extraModels = listing[extraModelName] || null
        if (extraModels) {
          extraModels.forEach(setModelImages)
        }
      })

      /**
       * APACSMDESK-58680
       * replacing proposal.listing.additionalPurchases complimentaries selectComplimentaries
       * with proposal.content.UpdateImageV2.OriGd.AdditionalPurchases Complimentaries SelectComplimentaries
       * 檢視修改圖片提案時改以 API 中未轉換的資料來表現加價購、贈品買就送、贈品任選，於轉換格式後替換
       */
      if (isConvertOriGd) {
        const updateImageV2 = _.get(proposal, 'content.UpdateImageV2')
        const convertedModels = _.get(
          updateImageV2 ? updateImageV2Converter(updateImageV2) : {},
          'models',
          []
        )
        clonedProposal.listing = {
          ...clonedProposal.listing,
          models: getMergedModels(
            // 預設從 proposal.content 取得賣場商品
            // 萬一找不到則改成從 listing 取得賣場商品
            _.isEmpty(convertedModels) ? modelsFromListing : convertedModels,
            _.get(clonedProposal, 'listing.models', [])
          )
        }
      }

      break
    }
    default: {
      break
    }
  }

  const loadingProposal = {
    ...clonedProposal,
    applicant: syncedApplicant,
    subStationLabel
  }

  return loadingProposal
}

/**
 *  >= 1000 的圖片長寬設為 1000,
 * 1000 > x >= 400 的圖片長寬設為 400,
 * 400 > x 為非法圖片
 * @param {*} image - 圖片
 */
const getValidSizeImages = images => _.map(
  images,
  image => {
    const _setThisImgSizeTo = targetDimension => _.merge(image, {
      height: targetDimension,
      width: targetDimension
    })

    return _setThisImgSizeTo(image.width >= 1000 ? 1000 : 400)
  }
)

/**
 *  將 model 中的 image 處理成要傳給 Endpoint 的格式
 * @param {*} model - 商品
 */
export const getPostFormatImages = model => {
  const images = _.get(model, 'images')
  const orderUniqueImages = getOrderUniqueImages(images)

  const validOrderUniqueImages = getValidSizeImages(orderUniqueImages)

  const firstOrderImages = _.get(model, 'firstOrderImages', null)
  if (_.isNull(firstOrderImages)) {
    return validOrderUniqueImages
  }

  const firstImgUrl = _.get(images, '[0].url')

  const primaryFirstOrderImages = firstOrderImages[firstImgUrl] || {}
  const currentFirstOrderImages = _.get(primaryFirstOrderImages, 'currentImages', [])

  const _hasValidUrl = ({ url }) => !!url
  const validFirstOrderImages = currentFirstOrderImages.filter(_hasValidUrl)

  return [
    ...validFirstOrderImages,
    ...validOrderUniqueImages
  ]
}

/**
 * 比對 images 是否有變更過
 * @param {object[]} originalImages
 * @param {object[]} proposingImages
 */
export const getImagesHasChanged = (originalImages, proposingImages) => {
  if (originalImages.length !== proposingImages.length) {
    return true
  }

  const _canFindSameImageIn = imagesPool => ({ url, order, width }) => _.isObject(_.find(imagesPool, { url, order, width }))

  return !originalImages.every(_canFindSameImageIn(proposingImages))
}

export const checkModelsHasChangedBy = counterpartModels => model => {
  const sku = _.get(model, 'sku')

  // init
  const counterpartModel = _.find(counterpartModels, { sku })
  const counterpartImages = getPostFormatImages(counterpartModel)

  // form
  const postFormatImages = getPostFormatImages(model)
  const formModelImages = _.get(model, 'images', [])

  const imagesHasChanged = getImagesHasChanged(
    counterpartImages,
    formModelImages
  )

  return imagesHasChanged
    ? {
      sku,
      images: postFormatImages
    }
    : null
}

/**
 * backend 那邊的 proposal type 是 `updateImageV2`，
 * SP 前端這邊的 proposal type 是 `updateImage`，
 * 但是因為目前 URL 之類的都是用同一個 shp 裡的常數，
 * 所以先暫時在前端與後端的溝通過程中主動更換 proposal type
 *
 * updateAttribute, updateListingAttribute 也是一樣的道理
 */
export const UPDATE_IMAGE_V2 = 'updateImageV2'
export const UPDATE_LISTING_ATTRIBUTE = 'updateListingAttribute'

const switchProposalTypeBetween = (originalType, targetType) => proposal => {
  const currentProposalType = _.get(proposal, 'type')
  const _setProposalTypeTo = targetType => _.set(proposal, 'type', targetType)

  if (currentProposalType === originalType) {
    _setProposalTypeTo(targetType)
    return proposal
  }

  if (currentProposalType === targetType) {
    _setProposalTypeTo(originalType)
    return proposal
  }

  return proposal
}

/**
 * 互換 `updateImage` 與 `updateImageV2`
 * 互換 updateAttribute, updateListingAttribute
 */
const switchUpdateImageVersion = switchProposalTypeBetween(PROPOSAL_TYPE.UPDATE_IMAGE, UPDATE_IMAGE_V2)
const switchUpdateAttribute = switchProposalTypeBetween(PROPOSAL_TYPE.UPDATE_ATTRIBUTE, UPDATE_LISTING_ATTRIBUTE)
export const switchProposalType = _.flow([switchUpdateImageVersion, switchUpdateAttribute])
